import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "layouts/Main";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  selectPortfolio,
  selectForecastTemplates,
  selectPortfolioPriceLimits,
} from "store/selectors";
import {
  fetchPortfolio,
  fetchAllForecastTemplates,
  fetchPortfolioPriceLimits,
} from "store/appSlice";
import {
  Box,
  IconButton,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  Stack,
  Modal,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClientSelect from "components/ClientSelect/ClientSelect";
import ClearIcon from "@mui/icons-material/Clear";
import SavedSampleSelect from "./SavedSampleSelect";
import {
  deletePortfolio,
  savePortfolio,
  savePortfolioPriceLimits,
} from "api/resources/portfolios";
import ConfirmationModal from "components/Modals/Confirmation";
import { ProtectedPaths } from "routes";
import Select from "components/Select";
import PriceLimitsTable from "./PriceLimitsTable";
import { tallinnTimeToUtc } from "helpers/timeIntervals";

const nordPoolAuctions = {
  1: "DayAhead",
  2: "SIDC_IntradayAuction1",
  3: "SIDC_IntradayAuction2",
  4: "SIDC_IntradayAuction3",
};

const EditPortfolio = () => {
  const [snackbar, setSnackbar] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState<number | undefined>(1);
  const [visibleInExternalApis, setVisibleInExternalApis] = useState(false);
  const [valuesChanged, setValuesChanged] = useState(false);
  const [selectedClients, setSelectedClients] = useState<
    { title: string; value: number }[]
  >([]);
  const [selectedSavedSamples, setSelectedSavedSamples] = useState<
    { title: string; value: number }[]
  >([]);
  const [periodPriceLimits, setPeriodPriceLimits] = useState([]);
  const [formValues, setFormValues] = useState({
    name: "",
    priceArea: "",
    nordpoolNames: [{ name: "", auctions: [] }],
    clients: [],
    prognoseTemplates: [],
    limitMin: null as number | null,
    limitMax: null as number | null,
  });
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const portfolio = useAppSelector(selectPortfolio);
  const portfolioPriceLimits = useAppSelector(selectPortfolioPriceLimits);
  const savedSamples = useAppSelector(selectForecastTemplates);

  useEffect(() => {
    dispatch(fetchPortfolio(Number(id)));
    dispatch(fetchPortfolioPriceLimits(Number(id), selectedAuction));
    dispatch(fetchAllForecastTemplates());
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(fetchPortfolioPriceLimits(Number(id), selectedAuction));
  }, [selectedAuction]);

  const snackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar(null);
  };

  useEffect(() => {
    if (portfolio) {
      const formValuesCopy = { ...formValues };

      formValuesCopy.name = portfolio.name;
      formValuesCopy.priceArea = portfolio.priceArea;
      formValuesCopy.nordpoolNames =
        portfolio.nordpoolNames.length > 0
          ? portfolio.nordpoolNames.map((np) => {
              return {
                name: np.name,
                auctions: np.nordpoolAuctions.map((npa) => {
                  return npa.id;
                }),
              };
            })
          : [{ name: "", auctions: [] }];

      formValuesCopy.limitMin = portfolio.limitMin ?? null;
      formValuesCopy.limitMax = portfolio.limitMax ?? null;

      const selectedClientsCopy = portfolio.clients.map((client: any) => {
        return { title: client.name, value: client.id };
      });

      const selectedSavedSamplesCopy = portfolio.prognoseTemplates.map(
        (template: any) => {
          return { title: template.name, value: template.id };
        },
      );

      setFormValues(formValuesCopy);
      setSelectedClients(selectedClientsCopy);
      setSelectedSavedSamples(selectedSavedSamplesCopy);
      setVisibleInExternalApis(portfolio.visibleInExternalApis ?? false);
    }
  }, [portfolio]);

  const handleAddAnotherRow = () => {
    const formValuesCopy = { ...formValues };

    const npPortfolioRows = formValuesCopy.nordpoolNames;

    npPortfolioRows.push({ name: "", auctions: [] });

    setFormValues(formValuesCopy);
  };

  const handleSelectAuction = (portfolioIndex: number, key: number) => {
    const formValuesCopy = { ...formValues };

    const npPortfolioRow = formValuesCopy.nordpoolNames[portfolioIndex];
    let npPortfolioRowAuctions = [...npPortfolioRow.auctions];

    if (npPortfolioRowAuctions.includes(key)) {
      npPortfolioRowAuctions = npPortfolioRowAuctions.filter((a) => a !== key);
    } else {
      npPortfolioRowAuctions.push(key);
    }

    npPortfolioRow.auctions = npPortfolioRowAuctions;

    setFormValues(formValuesCopy);
  };

  const handleRemovePortfolioRow = (portfolioIndex: number) => {
    const formValuesCopy = { ...formValues };

    const updatedNordpoolNames = [...formValuesCopy.nordpoolNames];
    updatedNordpoolNames.splice(portfolioIndex, 1);

    formValuesCopy.nordpoolNames = updatedNordpoolNames;

    setFormValues(formValuesCopy);
  };

  useEffect(() => {
    const formValuesCopy = { ...formValues };

    formValuesCopy.clients = selectedClients.map((sc) => {
      return sc.value;
    });

    formValuesCopy.prognoseTemplates = selectedSavedSamples.map((sss) => {
      return sss.value;
    });

    setFormValues(formValuesCopy);
  }, [selectedSavedSamples, selectedClients]);

  const handleSavePriceLimits = (noSnackbar?: boolean) => {
    const postData = {
      auctionId: selectedAuction,
      values: periodPriceLimits.map((pl) => {
        return {
          time: tallinnTimeToUtc(pl.time),
          buy: pl.buy === "" ? null : pl.buy,
          sell: pl.sell === "" ? null : pl.sell,
        };
      }),
    };

    savePortfolioPriceLimits(Number(id), postData)
      .then(() => {
        if (!noSnackbar) {
          setSnackbar({
            type: "success",
            message: "Price limits updated",
          });
        }
        setValuesChanged(false);
      })
      .catch((error) => {
        console.log(error);
        setSnackbar({
          type: "error",
          message: error.message,
        });
      });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    handleSavePriceLimits(true);

    savePortfolio(Number(id), { ...formValues, visibleInExternalApis })
      .then(() => {
        setSnackbar({
          type: "success",
          message: "Portfolio updated successfully",
        });
      })
      .catch(() => {
        setSnackbar({
          type: "error",
          message: "Error updating portfolio",
        });
      });
  };

  return (
    <MainLayout
      snackbar={{
        open: !!snackbar,
        handleClose: snackbarClose,
        type: snackbar?.type,
        message: snackbar?.message,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", paddingBottom: 2 }}>
        <IconButton sx={{ mr: "1rem" }} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h5">
          Edit portfolio
        </Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container columnSpacing={2} sx={{ mt: "1rem" }}>
          <Grid item sm={6}>
            <Box display="flex" gap={2} alignItems="center">
              <TextField
                id="portfolioName"
                name="portfolioName"
                label="Name"
                size="small"
                required
                value={formValues.name}
                sx={{ minWidth: 200, flex: 1 }}
                onChange={(e) => {
                  const formValuesCopy = { ...formValues };

                  formValuesCopy.name = e.target.value;

                  setFormValues(formValuesCopy);
                }}
              />
              <Select
                label="Price area"
                id="priceArea"
                name="priceArea"
                value={formValues.priceArea}
                minWidth={200}
                onChange={(e: any) => {
                  const formValuesCopy = { ...formValues };

                  formValuesCopy.priceArea = e.target.value;

                  setFormValues(formValuesCopy);
                }}
                options={[
                  { label: "Estonia", value: "EE" },
                  { label: "Latvia", value: "LV" },
                  { label: "Lithuania", value: "LT" },
                ]}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={visibleInExternalApis}
                    onChange={(e) => setVisibleInExternalApis(e.target.checked)}
                  />
                }
                label="Visible in external APIs"
                sx={{ whiteSpace: "nowrap" }}
              />
            </Box>
          </Grid>
          {formValues.nordpoolNames.map(
            (npPortfolio: any, portfolioIndex: number) => {
              return (
                <Box
                  key={"NPPortfolio-" + portfolioIndex}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  mt={2}
                  ml={2}
                >
                  <Grid item sm={12}>
                    <Grid container columnSpacing={2}>
                      <Grid item sm={3}>
                        <TextField
                          id="portfolioNameInNordpool"
                          name="portfolioNameInNordpool"
                          label="Portfolio name in Nord Pool"
                          size="small"
                          value={npPortfolio.name}
                          onChange={(e) => {
                            const formValuesCopy = { ...formValues };

                            formValuesCopy.nordpoolNames[portfolioIndex].name =
                              e.target.value;

                            setFormValues(formValuesCopy);
                          }}
                          required
                          sx={{ width: "100%", mr: "2rem" }}
                        />
                      </Grid>
                      <Grid item sm={9}>
                        <Grid container spacing={2}>
                          {Object.entries(nordPoolAuctions).map(
                            ([key, value]) => (
                              <Grid item sm={3} key={"NPAuction-" + key}>
                                <Box>
                                  <Button
                                    variant={
                                      npPortfolio.auctions.includes(Number(key))
                                        ? "contained"
                                        : "outlined"
                                    }
                                    disabled={
                                      formValues.nordpoolNames.length > 1 &&
                                      !npPortfolio.auctions.includes(
                                        Number(key),
                                      ) &&
                                      formValues.nordpoolNames.some((np) =>
                                        np.auctions.includes(Number(key)),
                                      )
                                    }
                                    sx={{ width: "100%", height: "100%" }}
                                    onClick={() =>
                                      handleSelectAuction(
                                        portfolioIndex,
                                        Number(key),
                                      )
                                    }
                                  >
                                    {value}
                                  </Button>
                                </Box>
                              </Grid>
                            ),
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    ml={2}
                  >
                    <IconButton
                      onClick={() => handleRemovePortfolioRow(portfolioIndex)}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Box>
                </Box>
              );
            },
          )}
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 2, ml: 2 }}
            onClick={handleAddAnotherRow}
          >
            Add another
          </Button>
        </Grid>
        <Divider sx={{ margin: "2rem 0" }} />
        <Box>
          <Typography variant="h5" component="h4" sx={{ mb: 2 }}>
            Supply plan limits
          </Typography>
          <Box display="flex" gap={2} maxWidth={420}>
            <TextField
              id="limitMin"
              name="limitMin"
              label="Min"
              size="small"
              type="number"
              inputProps={{ step: 0.1 }}
              value={formValues.limitMin ?? ""}
              sx={{ flex: 1 }}
              onChange={(e) => {
                const formValuesCopy = { ...formValues };
                formValuesCopy.limitMin =
                  e.target.value === "" ? null : Number(e.target.value);
                setFormValues(formValuesCopy);
              }}
            />
            <TextField
              id="limitMax"
              name="limitMax"
              label="Max"
              size="small"
              type="number"
              inputProps={{ step: 0.1 }}
              value={formValues.limitMax ?? ""}
              sx={{ flex: 1 }}
              onChange={(e) => {
                const formValuesCopy = { ...formValues };
                formValuesCopy.limitMax =
                  e.target.value === "" ? null : Number(e.target.value);
                setFormValues(formValuesCopy);
              }}
            />
          </Box>
        </Box>
        <Divider sx={{ margin: "2rem 0" }} />
        <Box>
          <Typography variant="h5" component="h4" sx={{ mb: 2 }}>
            Clients
          </Typography>
          <ClientSelect
            selectedClients={selectedClients}
            setSelectedClients={setSelectedClients}
            label="Clients"
            minWidth={420}
            maxWidth={420}
          />
        </Box>
        <Divider sx={{ margin: "2rem 0" }} />
        <Box>
          <Typography variant="h5" component="h4" sx={{ mb: 2 }}>
            Consumption forecast
          </Typography>
          <SavedSampleSelect
            savedSamples={savedSamples}
            selectedSavedSamples={selectedSavedSamples}
            setSelectedSavedSamples={setSelectedSavedSamples}
            label="Saved samples"
            minWidth={420}
            maxWidth={420}
          />
        </Box>
        <Divider sx={{ margin: "2rem 0" }} />
        <Box>
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Typography variant="h5" component="h4" sx={{ mr: 2 }}>
              Price limits
            </Typography>
            <Select
              label="Auction"
              id="auction"
              name="auction"
              minWidth={150}
              maxWidth={150}
              value={selectedAuction}
              onChange={(e: any) => {
                setSelectedAuction(e.target.value);
              }}
              options={[
                {
                  label: "Day-ahead",
                  value: 1,
                },
                {
                  label: "IDA1",
                  value: 2,
                },
                {
                  label: "IDA2",
                  value: 3,
                },
                {
                  label: "IDA3",
                  value: 4,
                },
              ]}
            />
            {valuesChanged && (
              <Button
                variant="contained"
                onClick={() => handleSavePriceLimits()}
                sx={{ ml: 2 }}
              >
                Save price limits
              </Button>
            )}
          </Box>
          <PriceLimitsTable
            periodPriceLimits={periodPriceLimits}
            setPeriodPriceLimits={setPeriodPriceLimits}
            portfolioPriceLimits={portfolioPriceLimits}
            setValuesChanged={setValuesChanged}
            selectedAuction={selectedAuction}
          />
        </Box>
        <Stack spacing={2} direction="row" sx={{ mt: 8 }}>
          <Button type="submit" variant="contained">
            Save portfolio
          </Button>
          <Button variant="outlined" onClick={() => setDeleteModal(true)}>
            Delete
          </Button>
        </Stack>
      </Box>
      <Modal open={deleteModal} onClose={() => setDeleteModal(null)}>
        <ConfirmationModal
          title={`Do you want to delete this portfolio?`}
          subtitle="This will delete the portfolio from the system. This action cannot be undone."
          buttonTitle="Delete"
          action={() => {
            deletePortfolio(Number(id)).then(() => {
              setDeleteModal(false);
              navigate(`/${ProtectedPaths.Portfolios}`);
            });
          }}
          setConfirmationModal={setDeleteModal}
        />
      </Modal>
    </MainLayout>
  );
};
export default EditPortfolio;
